
import { InvoiceApi, InvoiceDataType } from "@/api/InvoiceApi";
import { SettlementApi, SettlementDataType } from "@/api/SettlementApi";
import { BuyInvoiceApi, BuyInvoiceDataType } from "@/api/BuyInvoiceApi";
import { CreditnoteDataType } from "@/api/CreditnoteApi";
import { DebitnoteDataType } from "@/api/DebitnoteApi";
import { ExpenseApi, ExpenseDataType } from "@/api/ExpenseApi";
import { ProviderInvoiceApi, ProviderInvoiceDataType } from "@/api/ProviderInvoiceApi";
import { CurrencyDataType } from "@/api/CurrencyApi";
import { parseDate } from "./DocumentStatus";
import { ProviderRecibeApi } from "@/api/ProviderRecibeApi";
import { InvoiceFormDataKeys } from "@/generated/InvoiceFormDataKeys";
import { InvoiceRelatedAttributes } from "@/formTypes/InvoiceDataType";
import { SettlementFormDataKeys } from "@/generated/SettlementFormDataKeys";
import { SettlementRelatedAttributes } from "@/formTypes/SettlementDataType";
import { ProviderInvoiceFormDataKeys } from "@/generated/ProviderInvoiceFormDataKeys";
import { ProviderInvoiceRelatedAttributes } from "@/formTypes/ProviderInvoiceDataType";
import { BuyInvoiceFormDataKeys } from "@/generated/BuyInvoiceFormDataKeys";
import { buyInvoiceRelatedAttributes } from "@/formTypes/BuyInvoiceDataType";
import { ExpenseRelatedAttributes } from "@/formTypes/ExpenseDataType";
import { ExpenseFormDataKeys } from "@/generated/ExpenseFormDataKeys";
import moment from "moment";
import { QuoteApi, QuoteDataType } from "@/api/QuoteApi";
import { QuoteFormDataKeys } from "@/generated/QuoteFormDataKeys";
import { QuoteRelatedAttributes } from "@/formTypes/QuoteDataType";

export type SearchByReferenceDocumentsEntryType =
    | { kind: "invoice"; data: InvoiceDataType[] }
    | { kind: "settlement"; data: SettlementDataType[] }
    | { kind: "providerInvoice"; data: ProviderInvoiceDataType[] }
    | { kind: "buyInvoice"; data: BuyInvoiceDataType[] }
    | { kind: "creditnote"; data: CreditnoteDataType[] }
    | { kind: "debitnote"; data: DebitnoteDataType[] }
    | { kind: "expense"; data: ExpenseDataType[] }
    | { kind: "quote"; data: QuoteDataType[] }

export const REFERENCE_DOCUMENT_LABELS: Record<string, string> = {
    "invoice": "Factura",
    "quote": "Cotizacion",
    "settlement": "Liquidacion",
    "providerInvoice": "Factura Provedor",
    "buyInvoice": "Factura Electronica Compra",
    "creditnote": "Nota de Credito",
    "debitnote": "Nota de Debito",
    "expense": "Expense"
}

export type SearchByReferenceResultRowData = {
    id: string;
    numero: string;
    sequence: string;
    nombreFacturacion: string;
    fecha: Date;
    total: number;
    recibo?: string | null;
    reciboId?: string | null;
    currency: CurrencyDataType,
    pagada?: boolean,
    anulada?: boolean,
    exchangeRate: number,
    clientId?: string | null;
    providerId?: string | null;
    kind: "invoice" | "settlement" | "providerInvoice" | "buyInvoice" | "creditnote" | "debitnote" | "expense" | "quote"
};

export const performReferenceSearch = async (referenceNumber: string): Promise<SearchByReferenceResultRowData[]> => {
    const documents = await Promise.all([
        loadInvoiceData(referenceNumber),
        loadSettlementData(referenceNumber),
        loadProviderInvoiceData(referenceNumber),
        loadBuyInvoiceData(referenceNumber),
        loadExpenseData(referenceNumber),
        loadQuoteData(referenceNumber),
    ]);
    return documents.flatMap(d => d).sort((a, b) => {
        const dataA = moment(a.fecha)
        const dataB = moment(b.fecha)
        return dataB.unix() - dataA.unix()
    })
}

export const loadQuoteData = async (referenceNumber: string): Promise<SearchByReferenceResultRowData[]> => {
    const selectionSet = [
        ...QuoteFormDataKeys.filter(k => !QuoteRelatedAttributes.includes(k)),
        "currency.*",
        "client.*",
        "quoteProducts.*"
    ]

    const { items: quoteDate } = await QuoteApi.fetchAllByReferenceNumber(referenceNumber, {}, { selectionSet });
    return await normalizeReferenceDocuments({
        kind: "quote",
        data: quoteDate
    })
}

export const loadExpenseData = async (referenceNumber: string): Promise<SearchByReferenceResultRowData[]> => {
    const selectionSet = [
        ...ExpenseFormDataKeys.filter(k => !ExpenseRelatedAttributes.includes(k)),
        "currency.*",
        "expenseCategory.*"
    ]

    const { items: expenseData } = await ExpenseApi.fetchAllByReferenceNumber(referenceNumber, {}, { selectionSet });
    return await normalizeReferenceDocuments({
        kind: "expense",
        data: expenseData
    })
}

export const loadBuyInvoiceData = async (referenceNumber: string): Promise<SearchByReferenceResultRowData[]> => {
    const selectionSet = [
        ...BuyInvoiceFormDataKeys.filter(k => !buyInvoiceRelatedAttributes.includes(k)),
        "currency.*",
        "buyInvoiceProducts.*"
    ]

    const { items: buyInvoiceData } = await BuyInvoiceApi.fetchAllByReferenceNumber(referenceNumber, {}, { selectionSet });
    return await normalizeReferenceDocuments({
        kind: "buyInvoice",
        data: buyInvoiceData
    })
}

export const loadProviderInvoiceData = async (referenceNumber: string): Promise<SearchByReferenceResultRowData[]> => {
    const selectionSet = [
        ...ProviderInvoiceFormDataKeys.filter(k => !ProviderInvoiceRelatedAttributes.includes(k)),
        "currency.*",
        "provider.*",
        "providerNotes.*",
        "providerInvoicePayments.*",
        "providerRecibe.*",
    ]

    const { items: providerInvoicesData } = await ProviderInvoiceApi.fetchAllByReferenceNumber(referenceNumber, {}, { selectionSet });
    return await normalizeReferenceDocuments({
        kind: "providerInvoice",
        data: providerInvoicesData
    })
}

export const loadSettlementData = async (referenceNumber: string): Promise<SearchByReferenceResultRowData[]> => {
    const selectionSet = [
        ...SettlementFormDataKeys.filter(k => !SettlementRelatedAttributes.includes(k)),
        "currency.*",
        "client.*",
        "settlementPayments.*",
        "interestEntries.*",
        "settlementInterestEntries.*",
        "recibe.*"
    ]

    const { items: settlementsData } = await SettlementApi.fetchAllByReferenceNumber(referenceNumber, {}, { selectionSet });
    return await normalizeReferenceDocuments({
        kind: "settlement",
        data: settlementsData
    })
}

export const loadInvoiceData = async (referenceNumber: string): Promise<SearchByReferenceResultRowData[]> => {
    const selectionSet = [
        ...InvoiceFormDataKeys.filter(k => !InvoiceRelatedAttributes.includes(k)),
        "currency.*",
        "client.*",
        "creditnotes.*",
        "debitnotes.*",
        "invoicePayments.*",
        "interestEntries.*",
        "apliedCreditnote.*",
        "recibe.*"
    ]

    const { items: invoicesData } = await InvoiceApi.fetchAllByReferenceNumber(referenceNumber, {}, { selectionSet });
    return await normalizeReferenceDocuments({
        kind: "invoice",
        data: invoicesData
    })

}

export const normalizeReferenceDocuments = async (item: SearchByReferenceDocumentsEntryType): Promise<SearchByReferenceResultRowData[]> => {
    switch (item.kind) {
        case "invoice":
            return normalizeInvoice(item);
        case "settlement":
            return normalizeSettlement(item);
        case "providerInvoice":
            return normalizeProviderInvoice(item);
        case "buyInvoice":
            return await normalizeBuyInvoice(item);
        case "expense":
            return normalizeExpense(item);
        case "quote":
            return normalizeQuote(item);
        default:
            // Exhaustive check
            throw new Error(`Tipo no soportado: ${(item as any)?.kind}`);
    }
};

const normalizeQuote = ({ data }: Extract<SearchByReferenceDocumentsEntryType, { kind: "quote" }>): SearchByReferenceResultRowData[] => {
    return data.map(i => {
        const fecha = parseDate(`${i.fecha}`);
        const currency = i.currency;
        const nombreFacturacion = `${i.nombre_factura}`

        const quote: SearchByReferenceResultRowData = {
            id: `${i.id}`,
            numero: `${i.numero}`,
            sequence: `${i.numero}`,
            nombreFacturacion,
            fecha: fecha,
            total: Number(i.total || 0),
            currency: currency,
            exchangeRate: Number(i.exchange_rate || 1),
            clientId: `${i.clientId}`,
            kind: "quote"
        }
        return quote
    })

}

const normalizeInvoice = ({ data }: Extract<SearchByReferenceDocumentsEntryType, { kind: "invoice" }>): SearchByReferenceResultRowData[] => {
    return data.flatMap(i => {
        let documents: SearchByReferenceResultRowData[] = [];
        const fecha = parseDate(`${i.fecha}`);
        const currency = i.currency;
        const nombreFacturacion = `${i.nombre_factura}`

        const invoice: SearchByReferenceResultRowData = {
            id: `${i.id}`,
            numero: `${i.numero}`,
            sequence: `${i.sequence}`,
            nombreFacturacion,
            fecha: fecha,
            total: Number(i.total || 0),
            recibo: i.recibe ? i.recibe.numero : "",
            reciboId: `${i.recibeId}`,
            currency: currency,
            pagada: i.status == "pagada",
            anulada: i.anulada || false,
            exchangeRate: Number(i.exchange_rate || 1),
            clientId: `${i.clientId}`,
            kind: "invoice"
        }
        documents.push(invoice)
        if (i.creditnotes && i.creditnotes?.length > 0) {
            const creditnotes = i.creditnotes.map((c: CreditnoteDataType) => {
                const fecha = parseDate(`${c.created}`);
                const creditNote: SearchByReferenceResultRowData = {
                    id: `${c.id}`,
                    numero: `${c.numero}`,
                    sequence: `${c.sequence}`,
                    nombreFacturacion: nombreFacturacion,
                    fecha: fecha,
                    total: Number(c.total || 0),
                    currency: currency,
                    exchangeRate: Number(c.exchange_rate || 1),
                    clientId: `${c.clientId}`,
                    kind: "creditnote"
                }
                return creditNote
            });

            documents = [
                ...documents,
                ...creditnotes
            ]
        }
        if (i.debitnotes && i.debitnotes?.length > 0) {
            const debitNotes = i.debitnotes.map((d: DebitnoteDataType) => {
                const fecha = parseDate(`${d.created}`);
                const debitnote: SearchByReferenceResultRowData = {
                    id: `${d.id}`,
                    numero: `${d.numero}`,
                    sequence: `${d.sequence}`,
                    nombreFacturacion: nombreFacturacion,
                    fecha: fecha,
                    total: Number(d.total || 0),
                    currency: currency,
                    exchangeRate: Number(d.exchange_rate || 1),
                    clientId: `${d.clientId}`,
                    kind: "debitnote"
                }
                return debitnote
            });
            documents = [
                ...documents,
                ...debitNotes
            ]
        }
        return documents
    })

}

const normalizeSettlement = ({ data }: Extract<SearchByReferenceDocumentsEntryType, { kind: "settlement" }>): SearchByReferenceResultRowData[] => {
    return data.map(s => {
        const fecha = parseDate(`${s.fecha}`);
        const currency = s.currency;
        const nombreFacturacion = `${s.nombre_factura}`

        const settlement: SearchByReferenceResultRowData = {
            id: `${s.id}`,
            numero: `${s.numero}`,
            sequence: "",
            nombreFacturacion,
            fecha: fecha,
            total: Number(s.total || 0),
            recibo: s.recibe ? s.recibe.numero : "",
            reciboId: `${s.recibeId}`,
            currency: currency,
            pagada: s.status == "pagada",
            anulada: s.anulada || false,
            exchangeRate: Number(s.exchange_rate || 1),
            clientId: `${s.clientId}`,
            kind: "settlement"
        }
        return settlement
    })

}

const normalizeProviderInvoice = ({ data }: Extract<SearchByReferenceDocumentsEntryType, { kind: "providerInvoice" }>): SearchByReferenceResultRowData[] => {
    return data.map(s => {
        const fecha = parseDate(`${s.fecha}`);
        const currency = s.currency;

        const settlement: SearchByReferenceResultRowData = {
            id: `${s.id}`,
            numero: `${s.numero}`,
            sequence: "",
            nombreFacturacion: `${s.provider.nombre}`,
            fecha: fecha,
            total: Number(s.total || 0),
            recibo: s.providerRecibe ? s.providerRecibe.numero : "",
            reciboId: `${s.providerRecibeId}`,
            currency: currency,
            pagada: s.status == "pagada",
            exchangeRate: Number(s.exchange_rate || 1),
            providerId: `${s.providerId}`,
            kind: "providerInvoice"
        }
        return settlement
    })

}

const normalizeBuyInvoice = async ({ data }: Extract<SearchByReferenceDocumentsEntryType, { kind: "buyInvoice" }>): Promise<SearchByReferenceResultRowData[]> => {
    return await Promise.all(
        data.map(async (s) => {
            const fecha = parseDate(`${s.fecha}`);
            const currency = s.currency;
            let providerRecibe;
            if (s.providerRecibeId) {
                providerRecibe = await ProviderRecibeApi.get(`${s.providerRecibeId}`)
            }

            const buyInvoice: SearchByReferenceResultRowData = {
                id: `${s.id}`,
                numero: `${s.numero}`,
                sequence: "",
                nombreFacturacion: `${s.nombre_factura}`,
                fecha: fecha,
                total: Number(s.total || 0),
                recibo: providerRecibe ? `${providerRecibe.numero_recibo || ""}` : "",
                reciboId: `${s.providerRecibeId}`,
                currency: currency,
                pagada: s.status == "pagada",
                exchangeRate: Number(s.exchange_rate || 1),
                providerId: `${s.providerId}`,
                kind: "buyInvoice"
            }
            return buyInvoice
        })
    )

}

const normalizeExpense = ({ data }: Extract<SearchByReferenceDocumentsEntryType, { kind: "expense" }>): SearchByReferenceResultRowData[] => {
    return data.map(e => {
        const fecha = parseDate(`${e.fecha}`);
        const currency = e.currency;

        const expense: SearchByReferenceResultRowData = {
            id: `${e.id}`,
            numero: `${e.sequence || ""}`,
            sequence: "",
            nombreFacturacion: `${e.nombre}`,
            fecha: fecha,
            total: Number(e.monto || 0),
            currency: currency,
            exchangeRate: Number(e.exchange_rate || 1),
            kind: "expense"
        }
        return expense
    })

}